<template>
  <div class="after-sales-overview">
    <!-- 本月已完成工单 + 数据统计 -->
    <div class="row-2">
      <section class="panel">
        <h3 class="section-title"><span class="title-bar" />{{ d.monthCompleted.title }}</h3>
        <div class="stats-row stats-row--3">
          <div
            v-for="item in d.monthCompleted.cards"
            :key="item.key"
            class="stat-card"
            :class="'stat-card--' + item.theme"
          >
            <span class="stat-watermark" aria-hidden="true">
              <dashboard-icons :name="item.icon" :size="72" :color="statWatermarkColor(item.theme)" />
            </span>
            <div class="stat-head">
              <span class="stat-icon" :class="'stat-icon--' + item.theme">
                <dashboard-icons :name="item.icon" size="16" color="#fff" />
              </span>
              <span class="stat-title">{{ item.label }}</span>
            </div>
            <div class="stat-value-row">
              <span class="stat-num">{{ fmtNum(item.value) }}</span>
              <span class="stat-unit">{{ item.unit }}</span>
            </div>
            <div class="stat-trend trend" :class="item.momUp ? 'trend--up' : 'trend--down'">
              <span class="field-label">环比</span>
              <span class="field-value">{{ momText(item.mom) }}</span>
              <dashboard-icons
                :name="item.momUp ? 'trendUp' : 'trendDown'"
                size="12"
                :color="trendArrowColor(item.momUp)"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3 class="section-title"><span class="title-bar" />{{ d.statistics.title }}</h3>
        <div class="stats-row stats-row--3">
          <div
            v-for="item in d.statistics.cards"
            :key="item.key"
            class="stat-card"
            :class="'stat-card--' + item.theme"
          >
            <span class="stat-watermark" aria-hidden="true">
              <dashboard-icons :name="item.icon" :size="72" :color="statWatermarkColor(item.theme)" />
            </span>
            <div class="stat-head">
              <span class="stat-icon" :class="'stat-icon--' + item.theme">
                <dashboard-icons :name="item.icon" size="16" color="#fff" />
              </span>
              <span class="stat-title">{{ item.label }}</span>
            </div>
            <div class="stat-value-row">
              <span class="stat-num">{{ item.isAmount ? fmtAmount(item.value) : fmtNum(item.value) }}</span>
              <span class="stat-unit">{{ item.unit }}</span>
            </div>
            <div v-if="item.extraLabel" class="stat-extra">
              <span class="field-label">{{ item.extraLabel }}</span>
              <span class="field-value">{{ fmtNum(item.extraValue) }}</span>
              <span class="field-unit">{{ item.extraUnit }}</span>
            </div>
            <div v-else class="stat-trend trend" :class="item.momUp ? 'trend--up' : 'trend--down'">
              <span class="field-label">环比</span>
              <span class="field-value">{{ momText(item.mom) }}</span>
              <dashboard-icons
                :name="item.momUp ? 'trendUp' : 'trendDown'"
                size="12"
                :color="trendArrowColor(item.momUp)"
              />
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 工单费用 + 问题分类 / 用户评价 -->
    <div class="row-2 row-bottom">
      <section class="panel panel--fee col-left">
        <h3 class="section-title"><span class="title-bar title-bar--purple" />{{ d.workOrderFee.title }}</h3>

        <div
          v-for="card in d.workOrderFee.cards"
          :key="card.key"
          class="wo-card"
        >
            <div
              class="wo-card-head"
              :class="{ 'wo-card-head--bordered': card.blocks }"
            >
              <span class="wo-icon">
                <dashboard-icons :name="card.icon" size="18" color="#fff" />
              </span>
              <span class="wo-card-title">{{ card.title }}</span>
            </div>

            <!-- 已完成工单合计 -->
            <template v-if="card.key === 'completed'">
              <div class="wo-card-body wo-card-body--donut">
                <div class="wo-main">
                  <div class="wo-total-row">
                    <span class="wo-bullet" />
                    <span class="wo-total-label">{{ card.totalLabel }}</span>
                    <span class="wo-total-value">
                      <span class="num">{{ fmtNum(card.total) }}</span>
                      <span class="unit">{{ card.unit }}</span>
                    </span>
                    <span class="wo-mom trend" :class="card.momUp ? 'trend--up' : 'trend--down'">
                      <span class="field-label">环比</span>
                      <span class="field-value">{{ momText(card.mom) }}</span>
                      <dashboard-icons
                        :name="card.momUp ? 'trendUp' : 'trendDown'"
                        size="12"
                        :color="trendArrowColor(card.momUp)"
                      />
                    </span>
                  </div>
                  <div class="wo-breakdown">
                    <div class="wo-break-item">
                      <span class="field-label">{{ card.outWarranty.label }}</span>
                      <span class="field-value">{{ fmtNum(card.outWarranty.count) }}</span>
                      <span class="field-unit">{{ card.outWarranty.unit }}</span>
                      <span class="wo-ratio wo-ratio--blue">占比 {{ ratioText(card.outWarranty.ratio) }}%</span>
                    </div>
                    <div class="wo-break-item">
                      <span class="field-label">{{ card.inWarranty.label }}</span>
                      <span class="field-value">{{ fmtNum(card.inWarranty.count) }}</span>
                      <span class="field-unit">{{ card.inWarranty.unit }}</span>
                      <span class="wo-ratio wo-ratio--orange">占比 {{ ratioText(card.inWarranty.ratio) }}%</span>
                    </div>
                  </div>
                </div>
                <div
                  class="donut-chart"
                  :style="donutStyle(card.donutInRatio)"
                  role="img"
                  :aria-label="`保内占比${card.inWarranty.ratio}%，保外占比${card.outWarranty.ratio}%`"
                />
              </div>
            </template>

            <!-- 工单收入 / 支出 -->
            <template v-else-if="card.blocks">
              <div class="wo-total-row wo-total-row--fee">
                <span class="wo-bullet" />
                <span class="wo-total-label">{{ card.totalLabel }}</span>
                <span class="wo-total-value">
                  <span class="num">{{ fmtAmount(card.total) }}</span>
                  <span class="unit">{{ card.unit }}</span>
                </span>
                <span class="wo-mom trend" :class="card.momUp ? 'trend--up' : 'trend--down'">
                  <span class="field-label">环比</span>
                  <span class="field-value">{{ momText(card.mom) }}</span>
                  <dashboard-icons
                    :name="card.momUp ? 'trendUp' : 'trendDown'"
                    size="12"
                    :color="trendArrowColor(card.momUp)"
                  />
                </span>
              </div>
              <div class="fee-blocks">
                <div v-for="block in card.blocks" :key="block.title" class="fee-block">
                  <div class="fee-block-head">
                    <span class="fee-block-title">
                      <span class="fee-block-name">{{ block.title }}</span>
                      <span class="field-value">{{ fmtAmount(block.amount) }}</span>
                      <span class="field-unit">元</span>
                    </span>
                    <span class="fee-block-ratio" :class="'fee-block-ratio--' + block.color">
                      <span class="ratio-label">占比</span>
                      <span class="ratio-value">{{ ratioText(block.ratio) }}%</span>
                    </span>
                  </div>
                  <div class="fee-lines-row" :class="'fee-lines-row--' + (block.lines || []).length">
                    <div v-for="line in (block.lines || [])" :key="line.label" class="fee-line">
                      <span class="field-label">{{ line.label }}</span>
                      <span class="field-value">{{ fmtAmount(line.value) }}</span>
                      <span class="field-unit">元</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
      </section>

      <div class="col-right">
        <section class="panel panel--problems">
          <h3 class="section-title section-title--compact"><span class="title-bar title-bar--orange" />{{ d.problems.title }}</h3>
          <div class="problem-cards">
            <div v-for="card in d.problems.cards" :key="card.key" class="problem-card">
              <div
                class="problem-card-head"
                :class="{ 'problem-card-head--bordered': card.list }"
              >
                <span class="problem-icon">
                  <dashboard-icons :name="card.icon" size="18" color="#fff" />
                </span>
                <span class="problem-card-title">{{ card.title }}</span>
              </div>

              <template v-if="card.list">
                <div class="ranked-grid">
                  <div v-for="(col, cIdx) in rankedColumns(card.list)" :key="cIdx" class="ranked-col">
                    <div v-for="row in col" :key="row.no" class="ranked-row">
                      <span class="ranked-no">{{ row.no }}</span>
                      <span class="ranked-name">{{ row.name }}</span>
                      <span class="ranked-count">
                        <span class="count-num">{{ row.count }}</span>
                        <span class="count-unit">{{ row.unit }}</span>
                      </span>
                      <span class="ranked-ratio">
                        <span class="ratio-label">占比</span>
                        <span class="ratio-value">{{ ratioText(row.ratio) }}%</span>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </section>

        <section class="panel panel--review">
          <h3 class="section-title section-title--compact"><span class="title-bar" />{{ d.reviews.title }}</h3>
          <div class="review-board">
            <div class="review-grid">
              <div v-for="item in d.reviews.items" :key="item.key" class="review-item">
                <span class="review-dot" aria-hidden="true" />
                <span class="review-label">{{ item.label }}</span>
                <span class="review-score">
                  <span class="num">{{ item.score === null || item.score === undefined ? '—' : item.score }}</span>
                  <span class="unit">分</span>
                </span>
                <span class="review-count">{{ item.count }} 人评价</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardIcons from './DashboardIcons.vue'
import { formatDashboardNum, formatDashboardAmount } from './dashboardLeaseMock'
import { getDashboardAfterSalesOverview } from '@/api/dashboard'

/** 空数据模板（结构与后端返回一致，值全部归零；mom=null 显示 —） */
function emptyAfterSalesData() {
  const card = (key, label, unit, theme, icon, extra) => ({
    key, label, value: 0, unit, mom: null, momUp: null, theme, icon, ...(extra || {}),
  })
  return {
    monthCompleted: {
      title: '本月已完成的售后工单',
      cards: [
        card('install', '新装机', '台', 'blue', 'asInstall'),
        card('repair', '维修', '台', 'blue', 'asRepair'),
        card('filter', '滤芯安装', '台', 'blue', 'asFilter'),
      ],
    },
    statistics: {
      title: '数据统计',
      cards: [
        card('pending', '待处理的售后申请', '个', 'orange', 'asPending'),
        card('complaint', '本年度售后投诉', '起', 'orange', 'asComplaint'),
        card('engineer', '新增工程师', '名', 'blue', 'asEngineer', { extraLabel: '当前总数', extraValue: 0, extraUnit: '名' }),
      ],
    },
    workOrderFee: {
      title: '本年度工单类型和费用概况',
      cards: [
        {
          key: 'completed', title: '已完成工单合计（本年）', icon: 'asWorkOrder',
          totalLabel: '工单合计', total: 0, unit: '单', mom: null, momUp: null,
          inWarranty: { label: '保内工单数', count: 0, unit: '单', ratio: 0 },
          outWarranty: { label: '保外工单数', count: 0, unit: '单', ratio: 0 },
        },
        {
          key: 'income', title: '工单收入（本年）', icon: 'asIncome',
          totalLabel: '工单收入合计', total: 0, unit: '元', mom: null, momUp: null, isAmount: true,
          blocks: [],
        },
        {
          key: 'expense', title: '工单支出（本年）', icon: 'asExpense',
          totalLabel: '工单支出合计', total: 0, unit: '元', mom: null, momUp: null, isAmount: true,
          blocks: [],
        },
      ],
    },
    reviews: {
      title: '本年度用户评价',
      items: [
        { key: 'customer', label: '终端客户评价', score: null, count: 0 },
        { key: 'dealer', label: '经销商评价', score: null, count: 0 },
      ],
    },
    problems: {
      title: '售后问题分类概况',
      cards: [
        { key: 'faultPerf', title: '常见故障表现', icon: 'asFaultDoc', list: [] },
        { key: 'faultTypes', title: '常见故障类型', icon: 'asFaultType', list: [] },
      ],
    },
  }
}

export default {
  name: 'DashboardAfterSalesOverview',
  components: { DashboardIcons },
  data() {
    return {
      d: emptyAfterSalesData(),
      loading: false,
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fmtNum: formatDashboardNum,
    fmtAmount: formatDashboardAmount,
    momText(mom) {
      return mom === null || mom === undefined ? '—' : `${mom}%`
    },
    ratioText(ratio) {
      const n = Number(ratio)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await getDashboardAfterSalesOverview()
        const data = res && res.data
        if (!data) return
        const empty = emptyAfterSalesData()
        // 卡片主题/图标沿用空模板定义，仅合并数值字段
        const mergeCards = (tplCards, apiCards) => (tplCards || []).map(tc => {
          const hit = (apiCards || []).find(ac => ac.key === tc.key) || {}
          return { ...tc, ...hit, theme: tc.theme, icon: tc.icon, extraLabel: hit.extraLabel || tc.extraLabel, extraValue: hit.extraValue != null ? hit.extraValue : tc.extraValue, extraUnit: hit.extraUnit || tc.extraUnit }
        })
        const mergedFeeCards = (empty.workOrderFee.cards || []).map(tc => {
          const hit = ((data.workOrderFee && data.workOrderFee.cards) || []).find(ac => ac.key === tc.key) || {}
          return { ...tc, ...hit, icon: tc.icon }
        })
        const mergedProblems = (empty.problems.cards || []).map(tc => {
          const hit = ((data.problems && data.problems.cards) || []).find(ac => ac.key === tc.key) || {}
          return { ...tc, list: hit.list || [] }
        })
        this.d = {
          monthCompleted: { title: empty.monthCompleted.title, cards: mergeCards(empty.monthCompleted.cards, data.monthCompleted && data.monthCompleted.cards) },
          statistics: { title: empty.statistics.title, cards: mergeCards(empty.statistics.cards, data.statistics && data.statistics.cards) },
          workOrderFee: { title: empty.workOrderFee.title, cards: mergedFeeCards },
          reviews: {
            title: empty.reviews.title,
            items: (data.reviews && data.reviews.items) || empty.reviews.items,
          },
          problems: { title: empty.problems.title, cards: mergedProblems },
        }
      } catch (e) {
        // 加载失败保留空模板展示
      } finally {
        this.loading = false
      }
    },
    trendArrowColor(up) {
      return up ? '#fa8c16' : '#52c41a'
    },
    statWatermarkColor(theme) {
      const map = { blue: 'rgba(24,144,255,0.12)', orange: 'rgba(250,140,22,0.12)', purple: 'rgba(146,84,222,0.12)' }
      return map[theme] || map.blue
    },
    donutStyle(inRatio) {
      const inPct = Number(inRatio) || 0
      return {
        background: `conic-gradient(#fa8c16 0% ${inPct}%, #1890ff ${inPct}% 100%)`,
      }
    },
    rankedColumns(list) {
      const mid = Math.ceil(list.length / 2)
      return [list.slice(0, mid), list.slice(mid)]
    },
  },
}
</script>

<style scoped>
.field-label {
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.55;
}

.field-value {
  color: #262626;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.55;
}

.field-unit {
  color: #262626;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.55;
}

.after-sales-overview {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.row-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.row-bottom {
  align-items: stretch;
}

.row-bottom > .col-left,
.row-bottom > .col-right {
  min-height: 100%;
}

.col-left {
  min-width: 0;
  height: 100%;
}

.col-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;
}

.panel {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
  box-sizing: border-box;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.5;
}

.section-title--compact {
  margin-bottom: 12px;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #1890ff;
  border-radius: 1px;
  flex-shrink: 0;
}

.title-bar--purple {
  background: #9254de;
}

.title-bar--orange {
  background: #fa8c16;
}

.trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.trend--up .field-value {
  color: #fa8c16;
}

.trend--down .field-value {
  color: #52c41a;
}

/* 顶部统计卡 */
.stats-row {
  display: grid;
  gap: 12px;
}

.stats-row--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stat-card {
  position: relative;
  border-radius: 8px;
  padding: 14px 14px 12px;
  min-width: 0;
  overflow: hidden;
}

.stat-card--blue {
  background: #f0f6ff;
}

.stat-card--orange {
  background: #fff7e6;
}

.stat-watermark {
  position: absolute;
  top: 8px;
  right: 4px;
  pointer-events: none;
  line-height: 0;
}

.stat-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  position: relative;
  z-index: 1;
}

.stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon--blue {
  background: #1890ff;
}

.stat-icon--orange {
  background: #fa8c16;
}

.stat-title {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.45;
}

.stat-value-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}

.stat-num {
  font-size: clamp(22px, 2.3vw, 28px);
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.stat-unit {
  font-size: 13px;
  font-weight: 400;
  color: #8c8c8c;
  padding-bottom: 2px;
  line-height: 1.45;
}

.stat-trend,
.stat-extra {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  line-height: 1.55;
}

/* 工单费用区 */
.panel--fee {
  background: linear-gradient(180deg, #faf8ff 0%, #f6f0ff 100%);
  border-color: #efdbff;
}

.wo-card {
  background: #fff;
  border: 1px solid #efdbff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 14px;
}

.wo-card:last-child {
  margin-bottom: 0;
}

.wo-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.wo-card-head--bordered {
  padding-bottom: 12px;
  margin-bottom: 0;
  border-bottom: 1px solid #f0f0f0;
}

.wo-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #9254de;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wo-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.45;
}

.wo-card-body--donut {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
}

.wo-total-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 12px;
  line-height: 1.55;
}

.wo-total-row--fee {
  padding: 14px 0 16px;
  margin-bottom: 0;
  flex-wrap: nowrap;
}

.wo-total-row--fee .wo-mom {
  margin-left: auto;
  flex-shrink: 0;
}

.wo-total-row--fee .wo-total-value .num {
  font-size: clamp(20px, 2vw, 26px);
}

.wo-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9254de;
  flex-shrink: 0;
}

.wo-total-label {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  line-height: 1.55;
}

.wo-total-value .num {
  font-size: clamp(19px, 1.9vw, 25px);
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.wo-total-value .unit {
  font-size: 13px;
  color: #8c8c8c;
  margin-left: 4px;
  line-height: 1.45;
}

.wo-breakdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wo-break-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  line-height: 1.55;
}

.wo-ratio {
  font-size: 13px;
  font-weight: 600;
  margin-left: auto;
  line-height: 1.55;
}

.wo-ratio--blue {
  color: #1890ff;
}

.wo-ratio--orange {
  color: #fa8c16;
}

.donut-chart {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  mask: radial-gradient(circle, transparent 40%, #000 41%);
  -webkit-mask: radial-gradient(circle, transparent 40%, #000 41%);
}

.fee-blocks {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fee-block {
  background: #f0f5ff;
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
}

.fee-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  line-height: 1.55;
}

.fee-block-title {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.fee-block-name {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
}

.fee-block-ratio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  line-height: 1.55;
}

.fee-block-ratio .ratio-label {
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 400;
}

.fee-block-ratio .ratio-value {
  font-size: 13px;
  font-weight: 600;
}

.fee-block-ratio--blue .ratio-value {
  color: #1890ff;
}

.fee-block-ratio--orange .ratio-value {
  color: #fa8c16;
}

.fee-lines-row {
  display: grid;
  gap: 10px 16px;
  align-items: start;
}

.fee-lines-row--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.fee-lines-row--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.fee-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.55;
  white-space: nowrap;
}

/* 用户评价 */
.panel--review {
  flex: 0 0 auto;
}

.review-board {
  background: #f0f7ff;
  border-radius: 8px;
  padding: 14px 18px;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 32px;
  row-gap: 14px;
}

.review-item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
  line-height: 1.55;
}

.review-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1890ff;
  flex-shrink: 0;
}

.review-label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #262626;
  white-space: nowrap;
}

.review-score {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
}

.review-score .num {
  font-size: clamp(18px, 1.9vw, 24px);
  font-weight: 600;
  color: #1890ff;
  line-height: 1.2;
}

.review-score .unit {
  font-size: 13px;
  color: #262626;
  font-weight: 400;
  line-height: 1.45;
}

.review-count {
  flex-shrink: 0;
  margin-left: 10px;
  font-size: 13px;
  font-weight: 400;
  color: #262626;
  white-space: nowrap;
  line-height: 1.55;
}

/* 售后问题分类 */
.panel--problems {
  background: #fff7e6;
  border-color: #ffd591;
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
}

.problem-cards {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  min-height: 0;
}

.problem-card {
  background: #fff;
  border: 1px solid #ffe7ba;
  border-radius: 8px;
  padding: 12px 14px;
  line-height: 1.45;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.problem-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.problem-card-head--bordered {
  padding-bottom: 10px;
  margin-bottom: 0;
  border-bottom: 1px solid #f0f0f0;
}

.problem-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #fa8c16;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.problem-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.ranked-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 28px;
  padding-top: 10px;
  flex: 1;
  min-height: 0;
  align-items: stretch;
}

.ranked-col {
  min-width: 0;
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 6px;
}

.ranked-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 56px 84px;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  border-bottom: 1px dashed #f0f0f0;
  line-height: 1.5;
  min-height: 0;
}

.ranked-col .ranked-row:last-child {
  border-bottom: none;
}

.ranked-no {
  font-size: 12px;
  font-weight: 600;
  color: #fa8c16;
  line-height: 1.6;
}

.ranked-name {
  font-size: 13px;
  font-weight: 400;
  color: #262626;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranked-count {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  white-space: nowrap;
}

.ranked-count .count-num {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
}

.ranked-count .count-unit {
  font-size: 13px;
  font-weight: 400;
  color: #262626;
}

.ranked-ratio {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  white-space: nowrap;
}

.ranked-ratio .ratio-label {
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
}

.ranked-ratio .ratio-value {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
}

@media (max-width: 1200px) {
  .stats-row--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .row-2 {
    grid-template-columns: 1fr;
  }

  .fee-lines-row--4,
  .fee-lines-row--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .wo-card-body--donut {
    grid-template-columns: 1fr;
  }

  .donut-chart {
    margin: 0 auto;
  }
}

@media (max-width: 520px) {
  .stats-row--3,
  .review-grid,
  .ranked-grid {
    grid-template-columns: 1fr;
  }

  .review-board {
    padding: 14px 16px;
  }

  .review-item {
    flex-wrap: wrap;
  }

  .fee-lines-row--4,
  .fee-lines-row--3 {
    grid-template-columns: 1fr;
  }

  .wo-total-row--fee {
    flex-wrap: wrap;
  }

  .wo-total-row--fee .wo-mom {
    margin-left: 0;
    width: 100%;
  }
}
</style>
